import { useState } from 'react'
import { PurchaseRequest } from '../types'
import { useAuth } from '../hooks/useAuth'
import { rejectPurchaseRequestByVedouci } from '../lib/apiPhpAdapter'
import PurchaseRequestHistory from './PurchaseRequestHistory'
import { highlightTextMultiple } from '../utils/textHighlight'
import { protectedFileHref } from '../utils/fileDownload'
import { CancelRequestModal } from './CancelRequestModal'
import { getPurchaseStatusLabel, getPurchaseStatusClass } from '../lib/purchaseRequestStatus'
import './PurchaseRequestCard.css'
import '../styles/common-buttons.css'

interface PurchaseRequestCardProps {
  request: PurchaseRequest
  onAction?: (request: PurchaseRequest, action: string) => void
  showActions?: boolean
  getStatusLabel?: (status: string) => string
  getStatusClass?: (status: string) => string
  customActions?: React.ReactNode
  searchText?: string
  onClick?: () => void
}

function PurchaseRequestCard({ 
  request, 
  onAction, 
  showActions = false,
  getStatusLabel,
  getStatusClass,
  customActions,
  searchText,
  onClick
}: PurchaseRequestCardProps) {
  const { user } = useAuth()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)

  // Žadatel může zrušit jen před schválením vedoucím
  const canCancelRequest = (): boolean => {
    if (!user) return false
    if (request.created_by_id !== user.id) return false
    return request.status === 'pending_leader'
  }

  // Обработчик подтверждения отмены
  const handleConfirmCancel = async (reason: string) => {
    try {
      await rejectPurchaseRequestByVedouci(request.id, reason)
      setIsCancelModalOpen(false)
      // Обновляем список заявок через onAction, если он передан
      if (onAction) {
        onAction(request, 'reject-vedouci')
      } else {
        // Если onAction не передан, просто перезагружаем страницу
        window.location.reload()
      }
    } catch (error: any) {
      alert(error?.message || 'Chyba při zrušení žádosti')
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('cs-CZ', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatDateShort = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('cs-CZ', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  const defaultGetStatusLabel = getPurchaseStatusLabel
  const defaultGetStatusClass = getPurchaseStatusClass

  const statusLabel = getStatusLabel ? getStatusLabel(request.status) : defaultGetStatusLabel(request.status)
  const statusClass = getStatusClass ? getStatusClass(request.status) : defaultGetStatusClass(request.status)

  return (
    <div 
      id={`purchase-${request.id}`}
      className="purchase-request-card" 
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <div className="request-header">
        <div className="request-id">#{request.id.substring(0, 8)}</div>
        <span className={`status-badge ${statusClass}`}>
          {statusLabel}
        </span>
      </div>
      
      <div className="request-info">
        <div className="info-row">
          <span className="info-label">Středisko:</span>
          <span className="info-value">{highlightTextMultiple(request.center_name || 'Neznámé', searchText)}</span>
        </div>
        <div className="info-row">
          <span className="info-label">Předmět:</span>
          <span className="info-value">{highlightTextMultiple(request.subject, searchText)}</span>
        </div>
        {request.amount && (
          <div className="info-row">
            <span className="info-label">Částka:</span>
            <span className="info-value">{request.amount.toLocaleString('cs-CZ')} Kč</span>
          </div>
        )}
        <div className="info-row">
          <span className="info-label">Vytvořeno:</span>
          <span className="info-value">{formatDate(request.created_at)}</span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        {canCancelRequest() && (
          <button 
            className="btn-reject-small"
            onClick={(e) => {
              e.stopPropagation()
              setIsCancelModalOpen(true)
            }}
            title="Zrušit žádost"
            style={{ marginLeft: 'auto' }}
          >
            Zrušit
          </button>
        )}
        <button 
          className="spoiler-toggle"
          onClick={(e) => {
            e.stopPropagation()
            setIsExpanded(!isExpanded)
          }}
          aria-expanded={isExpanded}
        >
          <span>{isExpanded ? '▼' : '▶'}</span>
          <span>{isExpanded ? 'Skrýt detaily' : 'Zobrazit detaily'}</span>
        </button>
      </div>

      {isExpanded && (
        <div className="request-details-expanded">
          <div className="details-section">
            <h4>Základní informace</h4>
            <div className="info-row">
              <span className="info-label">Odpovědná osoba:</span>
              <span className="info-value">{highlightTextMultiple(request.created_by_name || '-', searchText)}</span>
            </div>
            {request.supervisor_name && (
              <div className="info-row">
                <span className="info-label">Vedoucí:</span>
                <span className="info-value">{highlightTextMultiple(request.supervisor_name, searchText)}</span>
              </div>
            )}
            {request.rejected_by_leader_reason && (
              <div className="info-row">
                <span className="info-label">Důvod zamítnutí vedoucím:</span>
                <span className="info-value">{highlightTextMultiple(request.rejected_by_leader_reason, searchText)}</span>
              </div>
            )}
            {request.cancelled_reason && (
              <div className="info-row">
                <span className="info-label">Důvod zrušení:</span>
                <span className="info-value">{highlightTextMultiple(request.cancelled_reason, searchText)}</span>
              </div>
            )}
            <div className="info-row">
              <span className="info-label">E-Mail:</span>
              <span className="info-value">{highlightTextMultiple(request.created_by_email || '-', searchText)}</span>
            </div>
            {request.phone && (
              <div className="info-row">
                <span className="info-label">Telefon:</span>
                <span className="info-value">{highlightTextMultiple(request.phone, searchText)}</span>
              </div>
            )}
            <div className="info-row">
              <span className="info-label">Popis:</span>
              <span className="info-value">{highlightTextMultiple(request.description, searchText)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Důvod objednání:</span>
              <span className="info-value">{highlightTextMultiple(request.reason, searchText)}</span>
            </div>
            {request.supplier && (
              <div className="info-row">
                <span className="info-label">Dodavatel:</span>
                <span className="info-value">{highlightTextMultiple(request.supplier, searchText)}</span>
              </div>
            )}
            {request.file_name && request.file_url && (
              <div className="info-row">
                <span className="info-label">Příloha:</span>
                <span className="info-value">
                  <a 
                    href={protectedFileHref(request.file_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {request.file_name}
                  </a>
                  <span style={{ marginLeft: '10px', fontSize: '0.9em' }}>
                    <a
                      href={protectedFileHref(request.file_url)}
                      download={request.file_name}
                      className="file-link"
                      title="Stáhnout soubor"
                    >
                      ⬇️ Stáhnout
                    </a>
                  </span>
                </span>
              </div>
            )}
          </div>

          <div className="details-section">
            <h4>Status a schválení</h4>
            {request.approved_by_economist_name && (
              <div className="info-row">
                <span className="info-label">Schváleno ekonomem:</span>
                <span className="info-value">
                  {request.approved_by_economist_name} ({formatDateShort(request.approved_by_economist_at)})
                </span>
              </div>
            )}
            {request.rejected_by_economist_name && (
              <div className="info-row">
                <span className="info-label">Zamítnuto ekonomem:</span>
                <span className="info-value">
                  {request.rejected_by_economist_name} ({formatDateShort(request.rejected_by_economist_at)})
                </span>
              </div>
            )}
            {request.deferred_by_name && (
              <div className="info-row">
                <span className="info-label">Odloženo:</span>
                <span className="info-value">
                  {request.deferred_by_name} ({formatDateShort(request.deferred_at)})
                </span>
              </div>
            )}
            {request.approved_by_director_name && (
              <div className="info-row">
                <span className="info-label">Schváleno ředitelem:</span>
                <span className="info-value">
                  {request.approved_by_director_name} ({formatDateShort(request.approved_by_director_at)})
                </span>
              </div>
            )}
            {request.rejected_by_director_name && (
              <div className="info-row">
                <span className="info-label">Zamítnuto ředitelem:</span>
                <span className="info-value">
                  {request.rejected_by_director_name} ({formatDateShort(request.rejected_by_director_at)})
                </span>
              </div>
            )}
            {request.accepted_by_referent_name && (
              <div className="info-row">
                <span className="info-label">Přijato referentem:</span>
                <span className="info-value">
                  {request.accepted_by_referent_name} ({formatDateShort(request.accepted_by_referent_at)})
                </span>
              </div>
            )}
            {request.ordered_by_referent_name && (
              <div className="info-row">
                <span className="info-label">Objednáno referentem:</span>
                <span className="info-value">
                  {request.ordered_by_referent_name} ({formatDateShort(request.ordered_by_referent_at)})
                </span>
              </div>
            )}
            {request.ready_by_referent_name && (
              <div className="info-row">
                <span className="info-label">Připraveno referentem:</span>
                <span className="info-value">
                  {request.ready_by_referent_name} ({formatDateShort(request.ready_by_referent_at)})
                </span>
              </div>
            )}
            {request.rejected_by_referent_at && (
              <div className="info-row">
                <span className="info-label">Zamítnuto referentem:</span>
                <span className="info-value">
                  {request.rejected_by_referent_name ? `${request.rejected_by_referent_name} ` : ''}
                  ({formatDateShort(request.rejected_by_referent_at)})
                </span>
              </div>
            )}
            {request.rejected_by_referent_reason && (
              <div className="info-row">
                <span className="info-label">Důvod zamítnutí:</span>
                <span className="info-value">{request.rejected_by_referent_reason}</span>
              </div>
            )}
            {request.completed_at && (
              <div className="info-row">
                <span className="info-label">Dokončeno:</span>
                <span className="info-value">{formatDateShort(request.completed_at)}</span>
              </div>
            )}
            {request.completion_comment && (
              <div className="info-row">
                <span className="info-label">Komentář k dokončení:</span>
                <span className="info-value">{highlightTextMultiple(request.completion_comment, searchText)}</span>
              </div>
            )}
            {request.notes && (
              <div className="info-row">
                <span className="info-label">Poznámky:</span>
                <span className="info-value">{highlightTextMultiple(request.notes, searchText)}</span>
              </div>
            )}
          </div>

          <PurchaseRequestHistory requestId={request.id} />
        </div>
      )}

      {(customActions || (showActions && onAction)) ? (
        <div className="request-actions" onClick={(e) => e.stopPropagation()}>
          {customActions || (onAction && (
            <>
              {request.status === 'new' && (
                <>
                  <button 
                    className="btn-approve"
                    onClick={(e) => {
                      e.stopPropagation()
                      onAction(request, 'approve')
                    }}
                  >
                    Schválit
                  </button>
                  <button 
                    className="btn-reject"
                    onClick={(e) => {
                      e.stopPropagation()
                      onAction(request, 'reject')
                    }}
                  >
                    Zamítnout
                  </button>
                  <button 
                    className="btn-defer"
                    onClick={(e) => {
                      e.stopPropagation()
                      onAction(request, 'defer')
                    }}
                  >
                    Odložit
                  </button>
                </>
              )}
              {request.status === 'deferred' && (
                <>
                  <button 
                    className="btn-approve"
                    onClick={(e) => {
                      e.stopPropagation()
                      onAction(request, 'approve')
                    }}
                  >
                    Schválit
                  </button>
                  <button 
                    className="btn-reject"
                    onClick={(e) => {
                      e.stopPropagation()
                      onAction(request, 'reject')
                    }}
                  >
                    Zamítnout
                  </button>
                </>
              )}
              {request.status === 'approved_by_economist' && (
                <>
                  <button 
                    className="btn-approve"
                    onClick={(e) => {
                      e.stopPropagation()
                      onAction(request, 'approve')
                    }}
                  >
                    Schválit
                  </button>
                  <button 
                    className="btn-reject"
                    onClick={(e) => {
                      e.stopPropagation()
                      onAction(request, 'reject')
                    }}
                  >
                    Zamítnout
                  </button>
                </>
              )}
            </>
          ))}
        </div>
      ) : null}

      {/* Модальное окно отмены заявки */}
      {isCancelModalOpen && (
        <CancelRequestModal
          isOpen={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          onConfirm={handleConfirmCancel}
          requestSubject={request.subject}
          warning="Opravdu chcete stáhnout tuto žádost? Vedoucí ji ještě neschválil."
          reasonLabel="Důvod zrušení"
        />
      )}
    </div>
  )
}

export default PurchaseRequestCard

